import { useCallback, useState, type ChangeEvent } from 'react';
import type { Regla } from './validacion';

type Valores = Record<string, string>;
type Reglas<T extends Valores> = Partial<Record<keyof T, Regla<T>>>;

function validar<T extends Valores>(valores: T, reglas: Reglas<T>): Partial<Record<keyof T, string>> {
  const errores: Partial<Record<keyof T, string>> = {};
  for (const clave of Object.keys(reglas) as (keyof T)[]) {
    const error = reglas[clave]?.(valores[clave] ?? '', valores);
    if (error) errores[clave] = error;
  }
  return errores;
}

/**
 * Un error se muestra recién cuando se deja el campo, y desaparece apenas se
 * corrige: marcar en rojo mientras la persona todavía está escribiendo molesta.
 */
export function useFormulario<T extends Valores>(inicial: T, reglas: Reglas<T>, largos: Partial<Record<keyof T, number>> = {}) {
  const [valores, setValores] = useState<T>(inicial);
  const [tocados, setTocados] = useState<Partial<Record<keyof T, boolean>>>({});

  const errores = validar(valores, reglas);

  const cambiar = useCallback((clave: keyof T, valor: string) => {
    const maximo = largos[clave];
    setValores((previos) => ({ ...previos, [clave]: maximo ? valor.slice(0, maximo) : valor }));
  }, [largos]);

  const campo = (clave: keyof T) => ({
    value: valores[clave],
    onChange: (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => cambiar(clave, e.target.value),
    onBlur: () => setTocados((t) => ({ ...t, [clave]: true })),
    error: tocados[clave] ? errores[clave] : undefined,
  });

  /** Marca todo como tocado y dice si se puede enviar. */
  const validarTodo = (): boolean => {
    setTocados(Object.fromEntries(Object.keys(valores).map((k) => [k, true])) as Partial<Record<keyof T, boolean>>);
    return Object.keys(errores).length === 0;
  };

  return { valores, cambiar, campo, validarTodo, errores };
}
